import * as React from 'react';
import { Box, Button, CircularProgress, Fab, Popover, TextField, Typography } from '@mui/material';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { InputBaseSx, PrimaryBtnSx, PrimarySupportBtnSx } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';
import SendIcon from '@mui/icons-material/Send';
import { ContactUsModel, ContactUsProviderInstance } from '../@shared/services/ContactUsProvider';
import { useSelector } from 'react-redux';
import { getIsAuthorized } from '../reducers/main/mainReducer';
import { useDispatch } from 'react-redux';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';
import { NotificationModel, NotificationModelType } from '../reducers/main/mainModel';


export const withSupport = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const [userEl, setUserEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(userEl);
  const [message, setMessage] = React.useState<string | undefined>();
  const [isSending, setIsSending] = React.useState<boolean>(false);

  const user = useSelector(getIsAuthorized)
  const dispatch = useDispatch();
  const onCloseUserDetails = () => {
    setUserEl(null);
  };

  const onSendMessage = React.useCallback(async () => {
    setIsSending(true);
    const data: ContactUsModel = {
      body: message,
      email: user?.email,
      name: user?.name
    }
    try {
      const res = await ContactUsProviderInstance.send(data)
      if (res.status !== 200) {
        dispatch(onThrowAlert({
          message: "An error occurred while sending your message. Please try again later or email us directly at contact@mlartisan.com.",
          lifeTime: 5000,
          type: NotificationModelType.Error
        } as NotificationModel));
      }
      else {
        dispatch(onThrowAlert({
          message: "Your message has been sent successfully. We'll contact you via email shortly.",
          lifeTime: 5000,
          type: NotificationModelType.Success
        } as NotificationModel));
      }
    }
    catch (e) {
      dispatch(onThrowAlert({
        message: "An error occurred while sending your message. Please try again later or email us directly at contact@mlartisan.com.",
        lifeTime: 5000,
        type: NotificationModelType.Error
      } as NotificationModel));
    }
    finally {
      onCloseUserDetails();
      setIsSending(false);
      setMessage(undefined);
    }
  }, [dispatch, message, user?.email, user?.name])

  return <>
    <Fab sx={{
      ...PrimarySupportBtnSx,
      position: 'absolute',
      bottom: 15,
      right: 15
    }} onClick={(e) => setUserEl(e.currentTarget)} aria-label="add">
      <ContactSupportIcon />
    </Fab>
    <Popover
      open={open}
      anchorEl={userEl}
      onClose={onCloseUserDetails}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: Palette.backgroundColorPrimary,
          padding: "15px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "15px"
        }
      }}
    >
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "250px",
        marginBottom: "15px",
        gap: "5px"
      }}>
        <Typography sx={{ fontSize: Sizes.pageThirdTitle, color: Palette.textColorPrimary }}>Do you have a question?</Typography>
        <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorPrimary }}>Send us a message or email us directly at contact@mlartisan.com. After you send a message, we'll get in touch with you via your email.</Typography>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          width: "100%"
        }}>
          <TextField
            sx={{ ...InputBaseSx }}
            id="support-message-textarea"
            label="Message"
            fullWidth
            multiline
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
        </Box>
        <Button disabled={isSending} sx={PrimaryBtnSx} onClick={onSendMessage} startIcon={<SendIcon />}>Send</Button>
        {isSending && <Box sx={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, background: Palette.btnPrimaryHover }}>
          <CircularProgress sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px',
            color: Palette.btnPrimary
          }} />
        </Box>}
      </Box>
    </Popover>
    <Component {...props as T} />
  </>
}
